import _extends from "@babel/runtime/helpers/extends";
import React, { forwardRef, useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles } from '../../../utils/styles';
import Tooltip from '../../utils/Tooltip';
import styles from './styles';
const useStyles = createUseStyles(styles, 'Text');
const Text = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    children,
    useTooltip,
    bold = false,
    medium = false,
    color,
    withDivider,
    isHighlighted,
    classes: classesProp,
    overrides: overridesProp,
    className: classNameProp,
    as = 'span',
    type = 'body',
    isTruncated = false,
    strikethrough = false,
    ...props
  } = _ref;
  const [tooltipContent, setTooltipContent] = useState(null);
  const defaultRef = useRef(null);
  const classes = useClasses(useStyles, classesProp);
  const rootClassName = classnames(classes.root, classNameProp, classes[type], {
    [classes.bold]: bold,
    [classes.medium]: !bold && medium,
    [classes.truncated]: isTruncated,
    [classes.divider]: withDivider,
    [classes.highlighted]: isHighlighted,
    [classes.strikethrough]: strikethrough
  });
  const override = getOverrides(overridesProp, Text.overrides);
  const rootProps = useMemo(() => ({
    ...props,
    ...override.root
  }), [override.root, props]);
  var _ref2 = /*#__PURE__*/React.createElement("span", null, children);
  useEffect(() => {
    const finalRef = ref || defaultRef;
    if (useTooltip && finalRef.current?.offsetWidth < finalRef.current?.scrollWidth) setTooltipContent(_ref2);
  }, [children, useTooltip, ref]);
  const style = useMemo(() => !!color ? {
    color
  } : {}, [color]);
  const textContainer = useMemo(() => {
    const Tag = as || 'span';
    return /*#__PURE__*/React.createElement(Tag, _extends({
      ref: ref || defaultRef,
      className: rootClassName,
      style: style
    }, rootProps), children);
  }, [as, children, ref, rootClassName, rootProps, style]);
  if (useTooltip) return /*#__PURE__*/React.createElement(Tooltip, {
    placement: "top",
    content: tooltipContent
  }, textContainer);
  return textContainer;
});
Text.overrides = ['root', 'Loader'];
process.env.NODE_ENV !== "production" ? Text.propTypes = {
  as: PropTypes.oneOf(['div', 'span', 'strong', 'em', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'blockquote', 'label', 'li']),
  className: PropTypes.string,
  type: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'subtitle1', 'subtitle', 'body1', 'body', 'button', 'caption', 'captionMedium', 'badges', 'overline']),
  color: PropTypes.string,
  overrides: PropTypes.object,
  children: PropTypes.node,
  isTruncated: PropTypes.bool,
  bold: PropTypes.bool,
  medium: PropTypes.bool,
  strikethrough: PropTypes.bool,
  useTooltip: PropTypes.bool,
  withDivider: PropTypes.bool,
  isHighlighted: PropTypes.bool
} : void 0;
export default /*#__PURE__*/React.memo(Text);